<template>
  <div class="grid grid-cols max-w-[1144px] max-w-176 w-full items-start self-stretch">
    <FormStepInputs
      v-if="!isLoading"
      :step="{
        stepName: name,
        formInputs: content,
      }"
    >
      <template #roadsidePanelTitle>
        <div class="flex gap-y-1">
          <PanelTitle
            v-if="!isLoading"
            v-bind="getFormInput(props.content, 'roadsidePanelTitle')"
            :icon="{ value: { src: rsaDisplayText?.icon, alt: 'driver icon' } }"
            :title="{ value: rsaDisplayText?.title }"
            :subTitle="{ value: rsaDisplayText?.subTitle }"
          ></PanelTitle>
        </div>
      </template>
      <template #actionBar>
        <ActionBar
          v-if="showPaymentButton && filteredFields"
          v-bind="filteredFields"
          :outerClass="actionBarContent.outerClass"
          :innerClass="actionBarContent.innerClass"
          :handleLinkClickEvent="true"
          @btnClick="(linkName) => onClick_ActionBarButton(linkName)"
        ></ActionBar>
      </template>
    </FormStepInputs>
  </div>
  <div v-show="isLoading">
    <Skeleton :blocks="skeletonBlocks" />
  </div>
  <RsaChangeRegistrationModal :policy="currentPolicy" :content="changeRegistrationContent?.fields || []" />
</template>
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { getFormInput } from '@/utils/form-input-helper';
import { ISitecoreFormGenericInput, OwnedAssistanceProduct } from '@/interfaces';
import { useAccountStore, useManageDirectDebitStore, useRoadsideAssistance } from '@/store';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import ActionBar from '@/components/ActionBar/ActionBar.vue';
import { RoadsideProductType } from '@/utils/roadside-helpers';
import { scrollToElement } from '@/utils/dom-helpers';
import { asyncComputed } from '@vueuse/core';
import { RSA_ROADSIDE_EXCESS_USE } from '@/utils/roadside-helpers';
import RsaChangeRegistrationModal from './RsaChangeRegistrationModal.vue';

const accountStore = useAccountStore();
const rsaStore = useRoadsideAssistance();
const route = useRoute();

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
  testPolicyId?: string;
}>();

const currentPolicy = ref<OwnedAssistanceProduct>();
const isLoading = ref<boolean>(!!props?.testPolicyId);
const actionBarContent = getFormInput(props.content, 'actionBar');
const changeRegistrationContent = getFormInput(props.content, 'changeRegistration');
const manageDirectDebitStore = useManageDirectDebitStore();
const showPaymentDetails = ref<boolean>(false);

enum AllActionButtons {
  btnPaymentDetails = 'btnPaymentDetails',
}

onMounted(async () => {
  const eligibleProducts = await manageDirectDebitStore.getPortfolioAssitanceProductsForDirectDebit();
  showPaymentDetails.value =
    !!currentPolicy.value && eligibleProducts.some((x) => x.assetLineItemId === currentPolicy.value?.assetLineItemId);
});

const filteredFields = computed(() => {
  if (!currentPolicy?.value && accountStore?.identityDetails?.portfolio?.assistanceProducts) {
    getCurrentPolicy();
  }

  if (currentPolicy?.value) {
    const tempFilteredFields = {
      fields: actionBarContent.fields,
    };

    return tempFilteredFields;
  }

  return null;
});

const showPaymentButton = computed(() => {
  if (currentPolicy?.value) {
    return (
      currentPolicy.value?.roadsidePolicyType !== RoadsideProductType.Cmo &&
      currentPolicy.value.productCode !== RSA_ROADSIDE_EXCESS_USE &&
      showPaymentDetails.value
    );
  }
  return false;
});

function getCurrentPolicy() {
  if (!accountStore.identityDetails?.portfolio?.assistanceProducts) return null;
  const policyId = route.query.policyId;
  const status = route.query.status;

  const usePolicyId = (policyId?.length ?? 0) > 0 ? policyId : props.testPolicyId;
  const tempPolicy = accountStore?.identityDetails?.portfolio?.assistanceProducts.find(
    (x) => x.policyId === usePolicyId && (!status ? true : x.status === status)
  );

  currentPolicy.value = tempPolicy;
  isLoading.value = false;

  return tempPolicy;
}

const getTitle = async (rsaPolicy: OwnedAssistanceProduct) => {
  return rsaPolicy?.roadsidePolicyType !== RoadsideProductType.Cmo && rsaPolicy?.productCode !== RSA_ROADSIDE_EXCESS_USE
    ? (rsaPolicy?.tierDescription ?? '')
    : ((await rsaStore.getRsaProductContent(rsaPolicy?.productCode))?.tierDescription ?? '');
};

const getSubTitle = (rsaPolicy: OwnedAssistanceProduct) => {
  return `${
    !rsaPolicy?.isLifestyle && rsaPolicy?.vehicleDetail
      ? `${rsaPolicy?.vehicleDetail?.manufacturingYear} ${rsaPolicy?.vehicleDetail?.make?.description} ${rsaPolicy?.vehicleDetail?.model?.description} (${rsaPolicy.vehicleDetail?.registrationNumber})`
      : ''
  }`;
};

const rsaDisplayText = asyncComputed(async () => {
  const tempPolicy = getCurrentPolicy();

  let displayDetails = {
    icon: '',
    title: '',
    subTitle: '',
  };

  if (tempPolicy) {
    displayDetails.icon = require('@/assets/icons/driver-icon-ss.svg');
    displayDetails.title = await getTitle(tempPolicy);
    displayDetails.subTitle = getSubTitle(tempPolicy);
  }

  return displayDetails;
});

const onClick_ActionBarButton = (linkItem: any) => {
  if (!currentPolicy?.value) return;
  if (linkItem.name === AllActionButtons.btnPaymentDetails) {
    scrollToElement(linkItem.link.value.href, 500, -100);
  }
};

const skeletonBlocks = [
  {
    height: 26,
    width: 338,
    class: 'mb-3',
  },
  {
    height: 26,
    width: 176,
    class: 'mb-3',
  },
];
</script>
